import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch, useNavigate } from '@tanstack/react-router'
import {
  Check,
  ChevronDown,
  Download,
  Loader2,
  Printer,
  RotateCcw,
  Save,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import type {
  MarketingPosterConfig,
  PosterConfig,
  PosterLayout,
  PosterLogoSize,
  PosterPurpose,
  PosterThemeId,
} from '@/types'
import { PosterCanvas, type PosterCanvasHandle } from './PosterCanvas'
import { LAYOUT_DIMS, POSTER_THEMES } from './posterThemes'
import { POSTER_TEMPLATES, defaultMarketingPosterConfig } from './posterTemplates'
import { exportPosterPng, openPosterPrintWindow } from '@/lib/posterExport'

// One studio screen, shared by all six purposes. The active purpose is
// stored in the URL search string so a deep link (e.g. from the
// loyalty settings page) lands in the right tab.
type StudioSearch = { purpose?: PosterPurpose }

const LAYOUT_OPTIONS: { id: PosterLayout; label: string }[] = [
  { id: 'a4', label: 'A4 portrait' },
  { id: 'tent', label: 'Table tent' },
  { id: 'card', label: 'Counter card' },
]

const LOGO_OPTIONS: { id: PosterLogoSize; label: string }[] = [
  { id: 'sm', label: 'S' },
  { id: 'md', label: 'M' },
  { id: 'lg', label: 'L' },
  { id: 'xl', label: 'XL' },
]

const POSTER_CONFIG_KEY = 'marketing.poster_config'

export function AdminPosterStudio() {
  const search = useSearch({ strict: false }) as StudioSearch
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const initialPurpose: PosterPurpose = isValidPurpose(search.purpose)
    ? search.purpose!
    : 'loyalty'
  const [purpose, setPurpose] = useState<PosterPurpose>(initialPurpose)

  // Keep ?purpose=… in the URL so deep-links + page refreshes stick.
  useEffect(() => {
    if (search.purpose !== purpose) {
      navigate({
        to: '/admin/marketing/posters',
        search: { purpose },
        replace: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purpose])

  // Load saved config + brand details (logo + business name pulled from
  // receipt settings, which is where the rest of the app reads them).
  const configQuery = useQuery({
    queryKey: ['poster-config'],
    queryFn: async () => {
      try {
        const res = await apiClient.getSetting(POSTER_CONFIG_KEY)
        return (res?.data ?? null) as Partial<MarketingPosterConfig> | null
      } catch (err) {
        // 404 = settings row hasn't been seeded yet on this tenant
        // (schema_patches.go inserts the defaults at boot). Fall back
        // to in-memory defaults so the studio still opens.
        const status = (err as { status?: number; response?: { status?: number } })
        const code = status?.status ?? status?.response?.status
        if (code === 404) return null
        throw err
      }
    },
  })
  const brandQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })

  // Drafts are kept per-purpose so switching tabs preserves unsaved work.
  const [drafts, setDrafts] = useState<MarketingPosterConfig>(() =>
    defaultMarketingPosterConfig(),
  )
  const [serverSnapshot, setServerSnapshot] = useState<MarketingPosterConfig>(() =>
    defaultMarketingPosterConfig(),
  )
  useEffect(() => {
    const merged = mergeWithDefaults(configQuery.data)
    setServerSnapshot(merged)
    setDrafts(merged)
  }, [configQuery.data])

  const brand = useMemo(() => {
    const all = (brandQuery.data?.data ?? {}) as Record<string, unknown>
    const businessName =
      (typeof all.receipt_business_name === 'string' &&
        (all.receipt_business_name as string)) ||
      (typeof all.restaurant_name === 'string' && (all.restaurant_name as string)) ||
      ''
    const logoUrl =
      (typeof all.receipt_logo_url === 'string' && (all.receipt_logo_url as string)) ||
      ''
    // Carried on `brand` rather than read separately by each consumer, because
    // the live preview and the PNG/print exports must agree. Reading it in two
    // places is how a poster ends up printing differently from what was
    // approved on screen.
    const brandedQr = all.branded_qr_enabled !== false
    return { businessName, logoUrl, brandedQr }
  }, [brandQuery.data])

  const cfg = drafts[purpose]
  const purposeLabel = POSTER_TEMPLATES.find((t) => t.purpose === purpose)?.label ?? purpose
  const isDirty = useMemo(
    () => JSON.stringify(drafts) !== JSON.stringify(serverSnapshot),
    [drafts, serverSnapshot],
  )

  const updateDraft = useCallback(
    (patch: Partial<PosterConfig>) => {
      setDrafts((prev) => ({
        ...prev,
        [purpose]: { ...prev[purpose], ...patch },
      }))
    },
    [purpose],
  )

  const resetCurrent = useCallback(() => {
    const tpl = POSTER_TEMPLATES.find((t) => t.purpose === purpose)
    if (!tpl) return
    setDrafts((prev) => ({ ...prev, [purpose]: { ...tpl.defaults } }))
  }, [purpose])

  // Save the entire JSONB blob (all six purposes) — the settings endpoint
  // stores the value as-is and individual purposes are nested inside.
  const saveMutation = useMutation({
    mutationFn: async (next: MarketingPosterConfig) => {
      await apiClient.updateSetting(POSTER_CONFIG_KEY, next)
    },
    onSuccess: () => {
      toast({ title: 'Poster saved' })
      queryClient.invalidateQueries({ queryKey: ['poster-config'] })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Save failed', description: msg, variant: 'destructive' })
    },
  })

  const canvasRef = useRef<PosterCanvasHandle | null>(null)
  const [previewScale, setPreviewScale] = useState(0.5)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)

  // Default zoom adapts to layout so the poster fits inside the preview
  // pane without manual zooming on first render.
  useEffect(() => {
    if (cfg.layout === 'a4') setPreviewScale(0.5)
    else if (cfg.layout === 'tent') setPreviewScale(0.7)
    else setPreviewScale(0.95)
  }, [cfg.layout])

  // ── Actions ────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    saveMutation.mutate(drafts)
  }, [drafts, saveMutation])

  const handlePrint = useCallback(() => {
    openPosterPrintWindow({ config: cfg, brand, purpose })
  }, [brand, cfg, purpose])

  const [exportingPng, setExportingPng] = useState(false)
  const handleDownloadPng = useCallback(async () => {
    setExportingPng(true)
    try {
      const safe = `${purpose}_${cfg.layout}_${cfg.themeId}`
      await exportPosterPng({
        config: cfg,
        brand,
        purpose,
        filename: `poster_${safe}.png`,
      })
      toast({ title: 'PNG downloaded' })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Could not export PNG', description: msg, variant: 'destructive' })
    } finally {
      setExportingPng(false)
    }
  }, [brand, cfg, purpose, toast])

  const loading = configQuery.isLoading || brandQuery.isLoading

  return (
    <div className="space-y-5">
      {/* Action bar — purpose pills + global actions */}
      <Card className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <PurposeSwitcher value={purpose} onChange={setPurpose} />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setResetConfirmOpen(true)}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset to template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={loading}
            >
              <Printer className="h-4 w-4 mr-1.5" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPng}
              disabled={loading || exportingPng}
            >
              {exportingPng ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1.5" />
              )}
              Download PNG
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1.5" />
              )}
              Save
              {isDirty && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-orange-500" />}
            </Button>
          </div>
        </div>
      </Card>

      {/* Editor + Preview */}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        {/* Controls */}
        <div className="space-y-5">
          {/* Layout switcher */}
          <Card className="p-4">
            <div className="mb-3">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Layout
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {LAYOUT_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => updateDraft({ layout: opt.id })}
                  className={cn(
                    'rounded-md border px-3 py-2.5 text-sm font-medium transition-colors',
                    cfg.layout === opt.id
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-background hover:bg-muted',
                  )}
                >
                  <div>{opt.label}</div>
                  <div className="text-sm font-normal opacity-70 mt-0.5">
                    {LAYOUT_DIMS[opt.id].wMm} × {LAYOUT_DIMS[opt.id].hMm} mm
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Theme picker */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Theme
              </div>
              <Badge variant="secondary" className="font-normal">
                {POSTER_THEMES.find((t) => t.id === cfg.themeId)?.name}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {POSTER_THEMES.map((theme) => {
                const isActive = theme.id === cfg.themeId
                return (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => updateDraft({ themeId: theme.id as PosterThemeId })}
                    className={cn(
                      'group relative overflow-hidden rounded-lg border text-left transition-all',
                      isActive
                        ? 'border-foreground shadow-sm'
                        : 'border-border hover:border-foreground/40',
                    )}
                  >
                    <div
                      className="h-16 w-full flex items-end p-2"
                      style={{ background: theme.swatch.bg }}
                    >
                      <div className="flex gap-1">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ background: theme.swatch.ink }}
                        />
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ background: theme.swatch.accent }}
                        />
                      </div>
                    </div>
                    <div className="px-2.5 py-2 bg-background">
                      <div className="text-sm font-semibold leading-tight">
                        {theme.name}
                      </div>
                      <div className="text-sm text-muted-foreground leading-tight mt-0.5">
                        {theme.tagline}
                      </div>
                    </div>
                    {isActive && (
                      <div className="absolute top-1.5 right-1.5 rounded-full bg-foreground text-background p-0.5">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </Card>

          {/* Content */}
          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Content
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="headline" className="text-sm text-muted-foreground">
                  Headline
                </Label>
                <Textarea
                  id="headline"
                  value={cfg.headline}
                  onChange={(e) => updateDraft({ headline: e.target.value })}
                  maxLength={120}
                  rows={2}
                  placeholder="Visit 5 times. The 5th is on us."
                />
                <CharCount value={cfg.headline} max={120} />
              </div>
              <div>
                <Label htmlFor="subhead" className="text-sm text-muted-foreground">
                  Sub-headline
                </Label>
                <Textarea
                  id="subhead"
                  value={cfg.subhead}
                  onChange={(e) => updateDraft({ subhead: e.target.value })}
                  maxLength={180}
                  rows={2}
                  placeholder="Earn a free reward on your next visit."
                />
                <CharCount value={cfg.subhead} max={180} />
              </div>
              <div>
                <Label htmlFor="qrCaption" className="text-sm text-muted-foreground">
                  Caption under QR
                </Label>
                <Input
                  id="qrCaption"
                  value={cfg.qrCaption}
                  onChange={(e) => updateDraft({ qrCaption: e.target.value })}
                  maxLength={60}
                  placeholder="Scan with your phone camera"
                />
              </div>
              <div>
                <Label htmlFor="footer" className="text-sm text-muted-foreground">
                  Footer (optional)
                </Label>
                <Input
                  id="footer"
                  value={cfg.footer}
                  onChange={(e) => updateDraft({ footer: e.target.value })}
                  maxLength={120}
                  placeholder="Thank you."
                />
              </div>
            </div>
          </Card>

          {/* Logo size */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Logo size
              </div>
              {!brand.logoUrl && (
                <span className="text-sm text-muted-foreground">
                  Upload your logo in Settings → General
                </span>
              )}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {LOGO_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => updateDraft({ logoSize: opt.id })}
                  className={cn(
                    'rounded-md border py-2.5 text-sm font-semibold',
                    cfg.logoSize === opt.id
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-background hover:bg-muted',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Card>

          {/* QR destination */}
          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              QR destination
            </div>
            <Input
              type="url"
              inputMode="url"
              value={cfg.qrTargetUrl}
              onChange={(e) => updateDraft({ qrTargetUrl: e.target.value })}
              placeholder="https://example.com/menu"
            />
            <p className="text-sm text-muted-foreground mt-1.5">
              Where customers go when they scan. Use a /q/&lt;slug&gt; short URL from Marketing QR codes if you
              want live scan analytics.
            </p>
          </Card>

          {/* Advanced */}
          <Card className="p-4">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="w-full flex items-center justify-between text-sm font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Advanced
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform',
                  advancedOpen ? 'rotate-180' : '',
                )}
              />
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-sm text-muted-foreground">Accent override</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="color"
                      value={cfg.accentOverride || '#ea580c'}
                      onChange={(e) => updateDraft({ accentOverride: e.target.value })}
                      className="w-10 h-10 p-1 cursor-pointer flex-shrink-0"
                    />
                    <Input
                      type="text"
                      value={cfg.accentOverride ?? ''}
                      onChange={(e) =>
                        updateDraft({
                          accentOverride: e.target.value.trim() || null,
                        })
                      }
                      placeholder="Theme default"
                      className="font-mono text-sm"
                    />
                    {cfg.accentOverride && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateDraft({ accentOverride: null })}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Leave blank to use the theme's default accent.
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show "how it works" steps</Label>
                    <p className="text-sm text-muted-foreground">
                      Adds a 1-2-3 strip beneath the QR.
                    </p>
                  </div>
                  <Switch
                    checked={cfg.showExplainer}
                    onCheckedChange={(v) => updateDraft({ showExplainer: v })}
                  />
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Preview */}
        <div className="space-y-3">
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Live preview
              </div>
              <div className="flex items-center gap-3 min-w-[180px]">
                <span className="text-sm text-muted-foreground w-10 text-right tabular-nums">
                  {Math.round(previewScale * 100)}%
                </span>
                <Slider
                  value={[previewScale * 100]}
                  min={25}
                  max={120}
                  step={5}
                  onValueChange={(v) => setPreviewScale((v[0] ?? 50) / 100)}
                  className="w-32"
                />
              </div>
            </div>
          </Card>
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 overflow-auto min-h-[60vh] flex items-start justify-center">
            <PosterCanvas
              ref={canvasRef}
              config={cfg}
              brand={brand}
              purpose={purpose}
              scale={previewScale}
            />
          </div>
        </div>
      </div>

      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset the {purposeLabel} poster?</DialogTitle>
            <DialogDescription className="leading-relaxed">
              This replaces your edits to the{' '}
              <span className="font-medium text-foreground">{purposeLabel}</span> poster with
              the original template. Your other posters aren't affected. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setResetConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                resetCurrent()
                setResetConfirmOpen(false)
              }}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset to template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────

function PurposeSwitcher({
  value,
  onChange,
}: {
  value: PosterPurpose
  onChange: (v: PosterPurpose) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {POSTER_TEMPLATES.map((tpl) => {
        const Icon = tpl.icon
        const isActive = tpl.purpose === value
        return (
          <button
            key={tpl.purpose}
            type="button"
            onClick={() => onChange(tpl.purpose)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium border transition-colors',
              isActive
                ? 'bg-foreground text-background border-foreground'
                : 'bg-background text-foreground border-border hover:bg-muted',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {tpl.label}
          </button>
        )
      })}
    </div>
  )
}

function CharCount({ value, max }: { value: string; max: number }) {
  const len = value.length
  const close = len > max * 0.85
  return (
    <div
      className={cn(
        'text-sm mt-1 text-right tabular-nums',
        close ? 'text-orange-600' : 'text-muted-foreground',
      )}
    >
      {len} / {max}
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────

// Hand-maintained rather than derived, and the compiler cannot help: a missing
// entry here is not a type error, it just makes ?purpose=<new> silently fall
// back to loyalty. Keep it in step with PosterPurpose.
function isValidPurpose(v: unknown): v is PosterPurpose {
  return (
    typeof v === 'string' &&
    ['loyalty', 'menu', 'reviews', 'instagram', 'website', 'dine_in', 'custom'].includes(v)
  )
}

function mergeWithDefaults(
  data: Partial<MarketingPosterConfig> | null | undefined,
): MarketingPosterConfig {
  const defaults = defaultMarketingPosterConfig()
  if (!data || typeof data !== 'object') return defaults
  const result = { ...defaults }
  for (const key of Object.keys(defaults) as PosterPurpose[]) {
    const supplied = data[key]
    if (supplied && typeof supplied === 'object') {
      result[key] = { ...defaults[key], ...supplied }
    }
  }
  return result
}
